"use strict";
// Copyright © Veeam Software Group GmbH
var __createBinding = (this && this.__createBinding) || (Object.create ? (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    var desc = Object.getOwnPropertyDescriptor(m, k);
    if (!desc || ("get" in desc ? !m.__esModule : desc.writable || desc.configurable)) {
      desc = { enumerable: true, get: function() { return m[k]; } };
    }
    Object.defineProperty(o, k2, desc);
}) : (function(o, m, k, k2) {
    if (k2 === undefined) k2 = k;
    o[k2] = m[k];
}));
var __setModuleDefault = (this && this.__setModuleDefault) || (Object.create ? (function(o, v) {
    Object.defineProperty(o, "default", { enumerable: true, value: v });
}) : function(o, v) {
    o["default"] = v;
});
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (k !== "default" && Object.prototype.hasOwnProperty.call(mod, k)) __createBinding(result, mod, k);
    __setModuleDefault(result, mod);
    return result;
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.useContextMenu = void 0;
const react_1 = __importStar(require("react"));
const core_1 = require("@veeam/core");
const CommandMenu_1 = require("../../CommandMenu");
const getAdditionalContentNode_1 = require("../../CommandMenu/helpers/getAdditionalContentNode");
const Menu_1 = require("../../Menu");
const Popover_1 = require("../../Popover");
const getPropsFromDecorators_1 = require("../../Toolbar/helpers/getPropsFromDecorators");
function useContextMenu({ items, payloadGetter }) {
    const { canAction } = (0, core_1.useGuard)();
    const [isContextMenuActive, setIsContextMenuActive] = (0, react_1.useState)(false);
    const [offsetPosition, setOffsetPosition] = (0, react_1.useState)({
        offsetX: 0,
        offsetY: 0
    });
    const bodyRect = document.body.getBoundingClientRect();
    const menuItemsRef = (0, react_1.useRef)([]);
    const formatContextMenu = (0, react_1.useCallback)((_a, payload) => {
        var { title, text, handler, items, decorators, disabled, hidden } = _a, restItem = __rest(_a, ["title", "text", "handler", "items", "decorators", "disabled", "hidden"]);
        const decoratorProps = (0, getPropsFromDecorators_1.getPropsFromDecorators)(decorators, payload || null, canAction);
        const isItemsDisabled = (items) => {
            if (!items) {
                return false;
            }
            return items.reduce((acc, item) => {
                if (item.separator) {
                    return acc;
                }
                if (item.items) {
                    return acc && isItemsDisabled(item.items);
                }
                return acc && Boolean(item.disabled);
            }, true);
        };
        const isItemsHidden = (items) => {
            if (!items) {
                return false;
            }
            return items.reduce((acc, item) => {
                if (item.separator) {
                    return acc;
                }
                if (item.items) {
                    return acc && isItemsHidden(item.items);
                }
                return acc && Boolean(item.hidden);
            }, true);
        };
        return Object.assign(Object.assign({}, restItem), { text: title || text, id: restItem.id ? restItem.id.toString() : undefined, disabled: disabled || decoratorProps.disabled || isItemsDisabled(items), hidden: hidden || decoratorProps.hidden || isItemsHidden(items), onClick: () => {
                if (!disabled) {
                    handler === null || handler === void 0 ? void 0 : handler();
                }
            }, items: items === null || items === void 0 ? void 0 : items.map((el) => formatContextMenu(el, payload)) });
    }, [canAction]);
    const menuItems = (0, react_1.useMemo)(() => {
        const payload = payloadGetter === null || payloadGetter === void 0 ? void 0 : payloadGetter();
        return items.reduce((formattedItems, item) => {
            if (!item.disabled) {
                return [...formattedItems, formatContextMenu(item, payload)];
            }
            return formattedItems;
        }, []);
    }, [payloadGetter, items, formatContextMenu]);
    const actions = (0, react_1.useMemo)(() => {
        return {
            show: () => setIsContextMenuActive(true),
            hide: () => setIsContextMenuActive(false),
            toggle: () => setIsContextMenuActive((prevState) => !prevState)
        };
    }, []);
    const renderContent = (0, react_1.useCallback)(({ actions }) => (react_1.default.createElement(CommandMenu_1.CommandMenuItems, { menuItems: menuItems, renderContentProps: { actions }, menuItemsRef: menuItemsRef })), [menuItems, menuItemsRef]);
    const contextMenu = (0, Popover_1.usePopover)({
        isActive: isContextMenuActive,
        onOutsideClick: () => {
            if (isContextMenuActive) {
                setIsContextMenuActive(false);
            }
        },
        config: [
            Object.assign({ xAxis: Popover_1.X_AXIS.insideLeft, yAxis: Popover_1.Y_AXIS.insideTop }, offsetPosition),
            {
                xAxis: Popover_1.X_AXIS.insideRight,
                yAxis: Popover_1.Y_AXIS.insideTop,
                offsetX: -(bodyRect.width - offsetPosition.offsetX),
                offsetY: offsetPosition.offsetY
            },
            {
                xAxis: Popover_1.X_AXIS.insideLeft,
                yAxis: Popover_1.Y_AXIS.insideBottom,
                offsetX: offsetPosition.offsetX,
                offsetY: -(bodyRect.height - offsetPosition.offsetY)
            },
            {
                xAxis: Popover_1.X_AXIS.insideRight,
                yAxis: Popover_1.Y_AXIS.insideBottom,
                offsetX: -(bodyRect.width - offsetPosition.offsetX),
                offsetY: -(bodyRect.height - offsetPosition.offsetY)
            }
        ],
        getAdditionalContentNode: () => (0, getAdditionalContentNode_1.getAdditionalContentNode)(menuItemsRef),
        render: (popoverRef) => {
            menuItemsRef.current = [popoverRef];
            return react_1.default.createElement(Menu_1.Menu, { ref: popoverRef }, react_1.default.createElement(react_1.default.Fragment, null, renderContent({ actions })));
        },
        target: { measure: () => document.body.getBoundingClientRect() }
    });
    return {
        element: contextMenu,
        show: (offsetPosition) => {
            if (!menuItems.length) {
                return;
            }
            if (offsetPosition) {
                setOffsetPosition(offsetPosition);
            }
            setIsContextMenuActive(true);
        },
        hide: () => {
            menuItemsRef.current = [];
            setIsContextMenuActive(false);
        }
    };
}
exports.useContextMenu = useContextMenu;
//# sourceMappingURL=hooks.js.map